// app/AppConfigProvider.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { setCachedTenant, getTenantSubdomain } from "./helpers/tenant";

const Ctx = createContext(null);
export const useAppConfig = () => useContext(Ctx);

const loadJson = async (p, fb = {}) => {
  try {
    // Tenant configs have stable URLs, unlike the content-hashed bundles, so a
    // browser can serve a months-old copy after a deploy. "no-cache" forces
    // revalidation but still allows a 304.
    const r = await fetch(p, { cache: "no-cache" });
    return r.ok ? await r.json() : fb;
  } catch {
    return fb;
  }
};
const tenantKey = () => {
  const sub = getTenantSubdomain();
  return ["www", "app"].includes(sub) ? "default" : sub;
};

export default function AppConfigProvider({ children }) {
  const [tenant, setTenant] = useState(null);

  // single source of truth for user (no local mirrors)
  const user = useSelector((s) => s.userProfile?.userDetails?.[0] || null);
  const role = user?.UserTypeId === 2 ? "agent" : "member"; // adjust USER_TYPE_AGENT=2
  const planName = user?.PlanName || ""; // you also derive below via hook

  useEffect(() => {
    (async () => {
      const key = tenantKey();
      const defaults = await loadJson("/configs/tenants/default.json", {});
      const overrides =
        key === "default"
          ? defaults
          : await loadJson(`/configs/tenants/${key}.json`, defaults);
      const t = {
        ...defaults,
        ...overrides,
        featureFlags: {
          ...(defaults.featureFlags || {}),
          ...(overrides.featureFlags || {}),
        },
        themeVars: {
          ...(defaults.themeVars || {}),
          ...(overrides.themeVars || {}),
        },
      };
      Object.entries(t.themeVars || {}).forEach(([k, v]) =>
        document.documentElement.style.setProperty(k, v),
      );
      if (t.id) {
        document.body.setAttribute("data-tenant", t.id);
      }
      setCachedTenant(t);
      setTenant(t);
    })();
  }, []);

  const [userPack, setUserPack] = useState({});
  useEffect(() => {
    (async () => {
      const u = await loadJson(`/configs/users/${role}.json`, {});
      setUserPack(u);
    })();
  }, [role]);

  const value = useMemo(() => {
    if (!tenant) return null;
    return {
      tenant,
      userPack, // role/plan entitlements from JSON
      flags: tenant.featureFlags || {},
      strings: tenant.strings || {},
      components: tenant.components || {},
      componentProps: tenant.componentProps || {},
      user,
      role,
      planName, // expose store user + quick role/plan
    };
  }, [tenant, userPack, user, role, planName]);

  if (!value) return null;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
